import {
  CheckBoxIcon,
  CheckBoxOutlineBlankIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  FirstPageIcon,
  IndeterminateCheckBoxIcon,
  LastPageIcon,
} from 'icons';

import { components } from '../components';
import { BLUE_2, BLUE_10, DARK_4, DARK_5, DARK_7, LIGHT_0, LIGHT_2 } from './tokens';

import type { CSSObject, Components, Theme } from '@mui/material';

/**
 * Evaluate a legacy `styleOverrides` slot, which MUI types as an interpolation and so may be either
 * a plain object or a function of the component's props. Every slot resolved below returns a plain
 * object, hence the `CSSObject` assertion.
 *
 * Each refreshed slot pairs this with its own override in an ARRAY rather than spreading the two
 * together, because several of the shadows are declared inside a nested selector (`&:hover`,
 * `&&.MuiTooltip-tooltip`). Spreading would either clobber that whole nested block or lose to it on
 * specificity; a later rule at the same specificity simply wins.
 */
const legacySlot = <Props,>(slot: unknown, props: Props): CSSObject =>
  (typeof slot === 'function' ? (slot as (slotProps: Props) => unknown)(props) : slot) as CSSObject;

/**
 * The Design System 2.0 component overrides (HNS-263: no elevation).
 *
 * DS 2.0 has no shadows. Surfaces are separated by a border or by their own contrast, never by
 * depth — see the card in Figma node 18187:17351, which is a plain 1px `Color/Light/2` outline.
 *
 * Only the shadow-bearing slots are touched; every other legacy override passes through untouched,
 * so this stays a readable diff against the legacy theme rather than a second full theme to keep in
 * step.
 *
 * NOT covered here: the ~35 shadows hard-coded in individual `styled()` files (Natalie, Event
 * Broadcast, insights charts, Drawer, Snackbar, Calendar). Those bypass the theme entirely and need
 * their own pass. `MuiSwitch`'s `boxShadow` is a focus ring rather than elevation, so it stays.
 *
 * It also carries the Phosphor `defaultProps` (HNS-262). The barrel swap reaches every icon the app
 * renders itself, but not the ones MUI injects for you — a `Checkbox` with no `icon` prop, an
 * `Autocomplete`'s clear button, an `Alert`'s dismiss. Those resolve inside MUI to
 * `@mui/icons-material` glyphs that nothing in `icons/` can touch, so with the flag on they would
 * have stayed Material in the middle of otherwise Phosphor forms. Each slot below hands MUI a barrel
 * icon instead, and because these live only in the refreshed theme, the legacy theme is untouched.
 *
 * Three known gaps, left deliberately rather than approximated:
 *
 *   - `MuiRadio`. MUI composes it from two stacked `SvgIcon`s and animates the inner one with a
 *     `transform` transition. A flat Phosphor `RadioButton` is a single path and loses the animation,
 *     so this one wants a designer's call on what the checked state should do.
 *   - `MuiTablePagination`. It exposes no icon slots at all; changing its arrows needs a custom
 *     `ActionsComponent`, which is a component change rather than a theme one.
 *   - `@mui/x-date-pickers`. A separate icon family with its own theming keys, not
 *     `@mui/icons-material`.
 */
export const refreshComponents: Components<Theme> = {
  ...components,

  /**
   * Dialog and Menu set `border: none` and lean on the shadow as their ONLY edge, so removing it
   * outright would leave them floating on the page with nothing to bound them. Both therefore swap
   * elevation for the same 1px outline the Figma card uses.
   *
   * That swap is EXTRAPOLATED: there is no DS 2.0 dialog or menu node to read, only the card. It
   * matches the system's stated rule (border instead of depth) but wants a designer's confirmation
   * before the flag goes on for real.
   */
  MuiDialog: {
    ...components.MuiDialog,
    styleOverrides: {
      ...components.MuiDialog?.styleOverrides,
      /**
       * The scrim goes BLACK — design feedback on HNS-366.
       *
       * The legacy scrim is `alpha(darkBlue, 0.49)`, i.e. `rgba(9, 30, 66, 0.49)`: a navy tint that
       * cools everything behind it. Design System 2.0 draws it as a plain black at 50% in every cell
       * of the dialog set, which is why `dialogBackdropRecipe` in `components/Dialog` is a bare
       * `bg-black/50` with no token behind it — one value, both themes.
       *
       * Restated here rather than shared with that recipe: this is Emotion and that is Tailwind, and
       * there is no token in between to point both at. Kept numerically identical on purpose, so a
       * legacy dialog and a DS 2.0 one dim the page by the same amount when they sit in the same
       * flow — which they do, since only some of the room's dialogs have been ported.
       */
      root: (props) => [
        legacySlot(components.MuiDialog?.styleOverrides?.root, props),
        { '& .MuiBackdrop-root': { backgroundColor: 'rgba(0, 0, 0, 0.5)' } },
      ],
      paper: (props) => [
        legacySlot(components.MuiDialog?.styleOverrides?.paper, props),
        { border: `1px solid ${LIGHT_2}`, boxShadow: 'none' },
      ],
    },
  },

  MuiMenu: {
    ...components.MuiMenu,
    styleOverrides: {
      ...components.MuiMenu?.styleOverrides,
      // The legacy slot declares its dark-mode shadow at the top level too (via a conditional
      // spread), so one top-level `none` covers both modes.
      paper: (props) => [
        legacySlot(components.MuiMenu?.styleOverrides?.paper, props),
        { border: `1px solid ${LIGHT_2}`, boxShadow: 'none' },
      ],
    },
  },

  /**
   * A dark chip on a light page — it separates on contrast, so the shadow just goes.
   *
   * The colours move too (HNS-366, raised on the muted-microphone notification, which is a tooltip
   * rather than an alert). `dark[100]` (#2D2C2C) and `grey[200]` are legacy-ramp values, not steps
   * on the DS 2.0 scale; `Dark/4` on `Light/0` is the nested-surface pair the refreshed panels use,
   * so a tooltip now reads as the same material as everything else floating above the room.
   *
   * `MuiTypography-root` is restated because the base slot sets it — a tooltip whose body is a
   * `<Typography>`, which the microphone one is, takes that rather than the root `color`. The arrow
   * follows the fill for the same reason it does in the base theme: its `::before` is a separate
   * element and does not inherit.
   */
  MuiTooltip: {
    ...components.MuiTooltip,
    styleOverrides: {
      ...components.MuiTooltip?.styleOverrides,
      tooltip: (props) => [
        legacySlot(components.MuiTooltip?.styleOverrides?.tooltip, props),
        {
          backgroundColor: DARK_4,
          color: LIGHT_0,
          '& .MuiTypography-root': { color: LIGHT_0 },
          '&&.MuiTooltip-tooltip': { boxShadow: 'none', '&.dark': { boxShadow: 'none' } },
        },
      ],
      tooltipArrow: (props) => [
        legacySlot(components.MuiTooltip?.styleOverrides?.tooltipArrow, props),
        { '& .MuiTooltip-arrow': { '&::before': { backgroundColor: DARK_4 } } },
      ],
    },
  },

  /** Hover lift only; the card keeps its outline from `MuiPaper`'s default `outlined` variant. */
  MuiCard: {
    ...components.MuiCard,
    styleOverrides: {
      ...components.MuiCard?.styleOverrides,
      root: (props) => [
        legacySlot(components.MuiCard?.styleOverrides?.root, props),
        { '&:hover': { boxShadow: 'none' } },
      ],
    },
  },

  /** The value label keeps its 1.5px primary border, so it stays legible without the lift. */
  MuiSlider: {
    ...components.MuiSlider,
    styleOverrides: {
      ...components.MuiSlider?.styleOverrides,
      root: (props) => [
        legacySlot(components.MuiSlider?.styleOverrides?.root, props),
        { '& .MuiSlider-valueLabel': { boxShadow: 'none' } },
      ],
    },
  },

  /**
   * The three slots below exist for the dark PANELS (HNS-263) and are no-ops in light mode.
   *
   * Each pins a fixed light-ramp grey rather than reading the mode off the theme, which was harmless
   * while dark surfaces held nothing but white icons. Inside a `Color/Dark/3` panel they render a
   * light card, a near-invisible tab label and a white input, so under the refresh they defer to the
   * mode-aware token instead. Light mode keeps its exact legacy value — the DS light ramp is a
   * separate change.
   */
  MuiPaper: {
    ...components.MuiPaper,
    styleOverrides: {
      ...components.MuiPaper?.styleOverrides,
      outlined: (props) => [
        legacySlot(components.MuiPaper?.styleOverrides?.outlined, props),
        props.theme.palette.mode === 'dark' ? { borderColor: props.theme.palette.divider } : {},
      ],
    },
  },

  /**
   * The unselected label first (the legacy slot pins `grey[700]`, a light-ramp value), and then the
   * SELECTED one, which the dark media-settings dialog made a contrast problem (HNS-367).
   *
   * MUI draws a selected tab in `palette.primary.main`, which the refresh moves to
   * `Color/Blue/Primary` (#004fff). On the `Color/Dark/3` panel this dialog now draws, that is 3.0:1
   * — enough for the indicator rail, which is a non-text affordance, and short of the 4.5:1 a label
   * needs. So in dark mode the label goes to `text.primary` and selection is carried by the rail plus
   * the weight the legacy slot already applies (600 against 500).
   *
   * Deliberately NOT a lighter blue: `refreshPalette`'s dark half leaves `primary` alone, so
   * introducing `Color/Blue/3` for tabs alone would put two accent blues on the same surface. Wants a
   * designer's confirmation either way.
   */
  MuiTab: {
    ...components.MuiTab,
    styleOverrides: {
      ...components.MuiTab?.styleOverrides,
      root: (props) => [
        legacySlot(components.MuiTab?.styleOverrides?.root, props),
        props.theme.palette.mode === 'dark'
          ? {
              color: props.theme.palette.text.secondary,
              '&.Mui-selected': { color: props.theme.palette.text.primary },
            }
          : {},
      ],
    },
  },

  /**
   * The audience-confirmation callout ("Mic & Camera Off for Now") is a `standardInfo` alert, and it
   * is the one alert that renders inside a dark panel — a `blue[50]` card with `grey[900]` body text
   * and a `grey[900]` title, all of which stay light regardless of the mode.
   *
   * Scoped to `standardInfo` and self-contained on purpose: it restates every colour it needs rather
   * than leaning on the root's text colour, so the other severities — which sit on pale fills and
   * would be unreadable in white — are left exactly as they were.
   */
  MuiAlert: {
    ...components.MuiAlert,
    // The dismiss button on every closable `Alert` and `Ribbon`. Neither passes a close icon, so
    // without this MUI reaches for its own internal `Close` and the one glyph on a refreshed banner
    // stays Material. Component-valued: MUI resolves this through `useSlot`'s `elementType`.
    defaultProps: {
      ...components.MuiAlert?.defaultProps,
      slots: {
        ...components.MuiAlert?.defaultProps?.slots,
        closeIcon: CloseIcon,
      },
    },
    styleOverrides: {
      ...components.MuiAlert?.styleOverrides,
      standardInfo: (props) => [
        legacySlot(components.MuiAlert?.styleOverrides?.standardInfo, props),
        props.theme.palette.mode === 'dark'
          ? {
              // `&&&` because the legacy `root` slot pins the text with `&:not(.MuiAlert-filled)`,
              // which outweighs a plain declaration here however late it lands. Without the bump the
              // card went dark blue while every word on it stayed `grey[900]`.
              '&&&': {
                backgroundColor: BLUE_10,
                color: LIGHT_0,
                '.MuiAlert-message': { color: 'inherit' },
                '.MuiAlertTitle-root': { color: 'inherit' },
                '.MuiAlert-icon': { color: BLUE_2 },
              },
            }
          : {},
      ],
    },
  },

  /**
   * The unchecked track is `grey[300]`, and so is the thumb MUI derives for it — the same colour, so
   * on a dark panel the control reads as one solid light pill with no thumb in it. Dropping the
   * track to `Dark/5` puts the thumb back.
   *
   * The ON state needs nothing here: the checked track and the hover ring both resolve from
   * `palette.primary.main`, which `refreshPalette` moves to `Color/Blue/Primary`.
   */
  MuiSwitch: {
    ...components.MuiSwitch,
    styleOverrides: {
      ...components.MuiSwitch?.styleOverrides,
      track: (props) => [
        legacySlot(components.MuiSwitch?.styleOverrides?.track, props),
        props.theme.palette.mode === 'dark' ? { backgroundColor: DARK_5 } : {},
      ],
    },
  },

  /**
   * Every form in the app renders `Checkbox` without an `icon`, so all three states come from MUI's
   * own `CheckBoxOutlineBlank`/`CheckBox`/`IndeterminateCheckBox`. MUI `cloneElement`s whatever it is
   * given to inject `fontSize`, which our wrappers pass straight through.
   *
   * Note for whoever sees a flag-on test failure here: this removes the `CheckBoxIcon` and
   * `CheckBoxOutlineBlankIcon` testids that `MultipleChoiceMultipleSelectAnswer.test.tsx` queries.
   * The suite runs flag-off so it stays green, but prefer `getByRole('checkbox', { checked })` if you
   * touch that file.
   */
  MuiCheckbox: {
    ...components.MuiCheckbox,
    defaultProps: {
      ...components.MuiCheckbox?.defaultProps,
      icon: <CheckBoxOutlineBlankIcon />,
      checkedIcon: <CheckBoxIcon />,
      indeterminateIcon: <IndeterminateCheckBoxIcon />,
    },
  },

  /**
   * `Inputs/Autocomplete` already passes its own `popupIcon`; the other five call sites (the
   * react-hook-form wrapper, `EmailInput`, the label and tag-category inputs, multiple-choice setup)
   * do not, and NOBODY passes `clearIcon` — so the clear button is the one glyph here that was
   * guaranteed to stay Material.
   */
  MuiAutocomplete: {
    ...components.MuiAutocomplete,
    defaultProps: {
      ...components.MuiAutocomplete?.defaultProps,
      popupIcon: <ChevronDownIcon />,
      clearIcon: <CloseIcon fontSize="small" />,
    },
  },

  /** The tab strip's scroll arrows, which only appear once the tabs overflow their container. */
  MuiTabs: {
    ...components.MuiTabs,
    defaultProps: {
      ...components.MuiTabs?.defaultProps,
      slots: {
        StartScrollButtonIcon: ChevronLeftIcon,
        EndScrollButtonIcon: ChevronRightIcon,
      },
    },
  },

  /** `PagePicker`'s arrows. `first`/`last` are the double-chevron ends, not the single steps. */
  MuiPaginationItem: {
    ...components.MuiPaginationItem,
    defaultProps: {
      ...components.MuiPaginationItem?.defaultProps,
      slots: {
        previous: ChevronLeftIcon,
        next: ChevronRightIcon,
        first: FirstPageIcon,
        last: LastPageIcon,
      },
    },
  },

  /**
   * `Dark/4` is the nested-surface step above the `Dark/3` panel — EXTRAPOLATED, as there is no DS
   * 2.0 dark input node to read.
   */
  MuiOutlinedInput: {
    ...components.MuiOutlinedInput,
    styleOverrides: {
      ...components.MuiOutlinedInput?.styleOverrides,
      root: (props) => [
        legacySlot(components.MuiOutlinedInput?.styleOverrides?.root, props),
        props.theme.palette.mode === 'dark'
          ? {
              backgroundColor: DARK_4,
              '.MuiOutlinedInput-notchedOutline': { borderColor: props.theme.palette.divider },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: DARK_7 },
              '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: props.theme.palette.divider },
            }
          : {},
      ],
    },
  },
};
